import React, { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { Container, Card, Form, Button, Row, Col, Alert, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { useNotification } from '../hooks/useNotification';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../utils/apiUtils';

const ProjectProposal = () => {
  const { notification, showSuccess, showError, dismiss } = useNotification();
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fundSourceOther, setFundSourceOther] = useState('');
  
  // Monitoring and Evaluation table data
  const [meTableData, setMeTableData] = useState({
    impact: { performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    outcome: { performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    output: { performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    activities: { performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' },
    input: { performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' }
  });
  
  // M&E free-text rows
  const makeEmptyMeRow = () => ({ objectives: '', performance_indicators: '', baseline_data: '', performance_target: '', data_source: '', collection_method: '', frequency: '', responsible: '' });
  const [meRows, setMeRows] = useState([makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow(), makeEmptyMeRow()]);
  const addMeRow = () => setMeRows(prev => [...prev, makeEmptyMeRow()]);
  const removeMeRow = () => setMeRows(prev => prev.length > 5 ? prev.slice(0, prev.length - 1) : prev);
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    projectLeader: '',
    projectLeaderHonorific: '',
    projectLeaderTasks: [''],
    assistantLeaders: [ { name: '', honorific: '', tasks: [''] } ],
    coordinators: [ { name: '', honorific: '', tasks: [''] } ],
    projectStaffs: [ { name: '', honorific: '', tasks: [''] } ],
    assistantProjectLeaders: '',
    rationale: '',
    objectives: '',
    expectedOutput: '',
    strategiesMethods: '',
    totalCost: '',
    fundSource: '',
    financialPlanDetails: '',
    functionalRelationships: '',
    monitoringEvaluation: '',
    sustainabilityPlan: '',
    requestType: '',
    eventType: '',
    extensionAgenda: '',
    sdg: [],
    officesInvolved: '',
    programsInvolved: '',
    partnerAgencies: '',
    beneficiaries: '',
    selectedProjectLeader: ''
  });

  // Financial Plan rows (editable table)
  const [financialPlanRows, setFinancialPlanRows] = useState([
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' },
    { description: '', quantity: '', unit: '', price: '', total: '' }
  ]);

  const addFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      const next = [...prev, { description: '', quantity: '', unit: '', price: '', total: '' }];
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const removeFinancialPlanRow = () => {
    setFinancialPlanRows(prev => {
      if (prev.length <= 4) return prev; // keep minimum 4
      const next = prev.slice(0, prev.length - 1);
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const updateFinancialRow = (index, key, value) => {
    setFinancialPlanRows(prev => {
      const sanitizeNumber = (v) => {
        if (v === null || v === undefined) return '';
        const cleaned = String(v).replace(/[^\d.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          return parts[0] + '.' + parts.slice(1).join('');
        }
        return cleaned;
      };
      const next = prev.map((r, i) => {
        if (i !== index) return r;
        if (key === 'price' || key === 'total') {
          return { ...r, [key]: sanitizeNumber(value) };
        }
        return { ...r, [key]: value };
      });
      // keep JSON in formData for backend
      setFormData(fd => ({ ...fd, financialPlanDetails: JSON.stringify(next) }));
      return next;
    });
  };

  const getFinancialPlanGrandTotal = () => {
    return financialPlanRows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
  };

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/users/eligible-participants');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        showError('Failed to load users. Please refresh the page.');
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [showError]);

  const extensionAgendaOptions = [
  'BatStateU Inclusive Social Innovation for Regional Growth (BISIG) Program',
  'Livelihood and other Entrepreneurship related on Agri-Fisheries (LEAF)',
  'Environment and Natural Resources Conservation, Protection and Rehabilitation Program',
  'Smart Analytics and Engineering Innovation',
  'Adopt-a Municipality/Barangay/School/Social Development Thru BIDANI Implementation',
  'Community Outreach',
  'Technical- Vocational Education and Training (TVET) Program',
  'Technology Transfer and Adoption/Utilization Program',
  'Technical Assistance and Advisory Services Program',
  "Parents' Empowerment through Social Development (PESODEV)",
  'Gender and Development',
  'Disaster Risk Reduction and Management and Disaster Preparedness and Response/Climate Change Adaptation (DRRM and DPR/CCA)'
];

  // Function to format number with commas and decimal places
  const formatNumberWithCommas = (value) => {
    // Remove all characters except digits and decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    // Split by decimal point
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Add commas to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // If there's a decimal part, limit to 2 decimal places
    if (decimalPart !== undefined) {
      const limitedDecimal = decimalPart.substring(0, 2);
      return formattedInteger + '.' + limitedDecimal;
    }
    
    return formattedInteger;
  };

  // Function to remove commas from number (keep decimal places)
  const removeCommas = (value) => {
    return value.replace(/,/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'sdg') {
      // For checkboxes, we need to toggle based on current state
      setFormData(prev => {
        const isCurrentlyChecked = prev.sdg.includes(value);
        const newSdg = isCurrentlyChecked
          ? prev.sdg.filter(item => item !== value)
          : [...prev.sdg, value];
        return {
          ...prev,
          sdg: newSdg
        };
      });
    } else if (name === 'fundSource') {
      setFormData(prev => ({
        ...prev,
        fundSource: value
      }));
    } else if (name === 'totalCost') {
      // Format totalCost with commas for display
      const formattedValue = formatNumberWithCommas(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle Project Leader dropdown selection
  const handleProjectLeaderChange = (selectedOption) => {
    if (selectedOption) {
      setFormData(prev => ({
        ...prev,
        selectedProjectLeader: selectedOption.value,
        projectLeader: selectedOption.user.fullname
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedProjectLeader: '',
        projectLeader: ''
      }));
    }
  };

  const updateArrayField = (field, index, key, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      if (key === 'name' && typeof value === 'object' && value !== null) {
        // Handle ReactSelect selection
        arr[index] = { ...arr[index], [key]: value.user.fullname };
      } else {
        // Handle regular input
      arr[index] = { ...arr[index], [key]: value };
      }
      return { ...prev, [field]: arr };
    });
  };

  const addArrayRow = (field) => {
    setFormData(prev => ({ ...prev, [field]: [ ...prev[field], { name: '', honorific: '', tasks: [''] } ] }));
  };

  const removeArrayRow = (field, index) => {
    setFormData(prev => {
      const arr = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: arr.length ? arr : [ { name: '', honorific: '', tasks: [''] } ] };
    });
  };

  // Add/remove tasks for a specific user
  const addTask = (field, userIndex) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[userIndex] = { ...arr[userIndex], tasks: [...arr[userIndex].tasks, ''] };
      return { ...prev, [field]: arr };
    });
  };

  const removeTask = (field, userIndex, taskIndex) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      if (arr[userIndex].tasks.length > 1) {
        arr[userIndex] = { ...arr[userIndex], tasks: arr[userIndex].tasks.filter((_, i) => i !== taskIndex) };
      }
      return { ...prev, [field]: arr };
    });
  };

  const updateTask = (field, userIndex, taskIndex, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      const tasks = [...arr[userIndex].tasks];
      tasks[taskIndex] = value;
      arr[userIndex] = { ...arr[userIndex], tasks };
      return { ...prev, [field]: arr };
    });
  };

  // Add/remove tasks for project leader
  const addProjectLeaderTask = () => {
    setFormData(prev => ({ ...prev, projectLeaderTasks: [...prev.projectLeaderTasks, ''] }));
  };

  const removeProjectLeaderTask = (taskIndex) => {
    setFormData(prev => {
      if (prev.projectLeaderTasks.length > 1) {
        return { ...prev, projectLeaderTasks: prev.projectLeaderTasks.filter((_, i) => i !== taskIndex) };
      }
      return prev;
    });
  };

  const updateProjectLeaderTask = (taskIndex, value) => {
    setFormData(prev => {
      const tasks = [...prev.projectLeaderTasks];
      tasks[taskIndex] = value;
      return { ...prev, projectLeaderTasks: tasks };
    });
  };

  // Get available users for dropdowns (excluding already selected ones)
  const getAvailableUsers = (excludeField, currentIndex = null) => {
    const selectedNames = new Set();
    
      // Always exclude project leader from all other fields
      if (formData.selectedProjectLeader) {
        const projectLeader = users.find(u => u.user_id === formData.selectedProjectLeader);
        if (projectLeader) selectedNames.add(projectLeader.fullname);
      }
    
    // Always exclude assistant leaders from all other fields
    formData.assistantLeaders.forEach(leader => {
      if (leader.name) selectedNames.add(leader.name);
    });
    
    // Always exclude coordinators from all other fields
    formData.coordinators.forEach(coord => {
      if (coord.name) selectedNames.add(coord.name);
    });

    // Always exclude project staff from all other fields
    formData.projectStaffs.forEach(staff => {
      if (staff.name) selectedNames.add(staff.name);
    });
    
    // For the same field type, exclude other entries in that field (for multi-select fields)
    if (excludeField === 'assistantLeaders' && currentIndex !== null) {
      formData.assistantLeaders.forEach((leader, index) => {
        if (index !== currentIndex && leader.name) {
          selectedNames.add(leader.name);
        }
      });
    }
    
    if (excludeField === 'coordinators' && currentIndex !== null) {
      formData.coordinators.forEach((coord, index) => {
        if (index !== currentIndex && coord.name) {
          selectedNames.add(coord.name);
        }
      });
    }

    if (excludeField === 'projectStaffs' && currentIndex !== null) {
      formData.projectStaffs.forEach((staff, index) => {
        if (index !== currentIndex && staff.name) {
          selectedNames.add(staff.name);
        }
      });
    }
    
    return users
      .filter(user => !selectedNames.has(user.fullname))
      .map(user => ({
        value: user.user_id,
        label: `${user.fullname} (${user.role})`,
        user: user
      }));
  };

  // Handle form submission - show modal first
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  // Handle actual submission after modal confirmation
  const handleConfirmSubmit = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
    setShowConfirmModal(false);

      // If STF or MDS was selected, save project immediately then redirect to Line-Item Budget form
      if (formData.fundSource === 'STF' || formData.fundSource === 'MDS') {
        // Create the same payload as non-STF/MDS projects
        const payload = {
          request_type: formData.requestType === 'client' ? 'Client Requested' : 'Department Initiative',
          event_type: formData.eventType,
          title: formData.title,
          location: formData.location,
          start_date: formData.startDate,
          end_date: formData.endDate,
          start_time: formData.startTime,
          end_time: formData.endTime,
          extension_agenda: [formData.extensionAgenda],
          sdg_goals: formData.sdg,
          offices_involved: formData.officesInvolved,
          programs_involved: formData.programsInvolved,
          partner_agencies: formData.partnerAgencies,
          beneficiaries: formData.beneficiaries,
          total_cost: removeCommas(formData.totalCost),
          fund_source: formData.fundSource === 'Others' 
            ? [`Others: ${fundSourceOther}`] 
            : (formData.fundSource ? [formData.fundSource] : []),
          rationale: formData.rationale,
          objectives: formData.objectives,
          expected_output: formData.expectedOutput,
          strategies_methods: formData.strategiesMethods,
          financial_plan_details: formData.financialPlanDetails,
          functional_relationships: formData.functionalRelationships,
          monitoring_evaluation_table: { rows: meRows },
          sustainability_plan: formData.sustainabilityPlan,
          // User selections with tasks - SAME AS NON-STF/MDS
          project_leader_id: formData.selectedProjectLeader,
          project_leader_name: (formData.projectLeaderHonorific ? `${formData.projectLeaderHonorific} ` : '') + formData.projectLeader + (formData.projectLeaderTasks.filter(t => t.trim()).length > 0 ? ` - ${formData.projectLeaderTasks.filter(t => t.trim()).join('; ')}` : ''),
          project_leader_honorific: formData.projectLeaderHonorific,
          project_leader_task: formData.projectLeaderTasks.filter(t => t.trim()).join('; '),
          assistant_project_leader_ids: formData.assistantLeaders
            .filter(leader => leader.name)
            .map(leader => {
              const user = users.find(u => u.fullname === leader.name);
              return user ? user.user_id : null;
            }),
          assistant_project_leader_names: formData.assistantLeaders
            .filter(leader => leader.name)
            .map(leader => {
              const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
              return (leader.honorific ? `${leader.honorific} ` : '') + leader.name + (tasksStr ? ` - ${tasksStr}` : '');
            }),
          assistant_project_leader_honorifics: formData.assistantLeaders
            .filter(leader => leader.name)
            .reduce((honorifics, leader, index) => {
              const user = users.find(u => u.fullname === leader.name);
              if (user && leader.honorific) {
                honorifics[user.user_id] = leader.honorific;
              } else if (leader.honorific) {
                honorifics[index] = leader.honorific;
              }
              return honorifics;
            }, {}),
          assistant_project_leader_tasks: formData.assistantLeaders
            .filter(leader => leader.name)
            .reduce((tasks, leader, index) => {
              const user = users.find(u => u.fullname === leader.name);
              const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
              if (tasksStr) {
                if (user) {
                  tasks[user.user_id] = tasksStr;
                } else {
                  // If no user found, use index as key to preserve task
                  tasks[`assistant_${index}`] = tasksStr;
                }
              }
              return tasks;
            }, {}),
          coordinator_ids: [...formData.coordinators]
            .filter(coord => coord.name)
            .map(coord => {
              const user = users.find(u => u.fullname === coord.name);
              return user ? user.user_id : null;
            }),
          coordinator_names: [...formData.coordinators]
            .filter(coord => coord.name)
            .map(coord => {
              const tasksStr = coord.tasks.filter(t => t.trim()).join('; ');
              return coord.name + (tasksStr ? ` - ${tasksStr}` : '');
            }),
          coordinator_tasks: [...formData.coordinators]
            .filter(coord => coord.name)
            .reduce((tasks, coord, index) => {
              const user = users.find(u => u.fullname === coord.name);
              const tasksStr = coord.tasks.filter(t => t.trim()).join('; ');
              if (tasksStr) {
                if (user) {
                  tasks[user.user_id] = tasksStr;
                } else {
                  // If no user found, use index as key to preserve task
                  tasks[`coordinator_${index}`] = tasksStr;
                }
              }
              return tasks;
            }, {}),
          // New: project staff fields
          project_staff_ids: formData.projectStaffs
            .filter(staff => staff.name)
            .map(staff => {
              const user = users.find(u => u.fullname === staff.name);
              return user ? user.user_id : null;
            })
            ,
          project_staff_names: formData.projectStaffs
            .filter(staff => staff.name)
            .map(staff => (staff.honorific ? `${staff.honorific} ` : '') + staff.name),
          project_staff_honorifics: formData.projectStaffs
            .filter(staff => staff.name)
            .reduce((honorifics, staff, index) => {
              const user = users.find(u => u.fullname === staff.name);
              if (user && staff.honorific) {
                honorifics[user.user_id] = staff.honorific;
              } else if (staff.honorific) {
                honorifics[index] = staff.honorific;
              }
              return honorifics;
            }, {}),
          project_staff_tasks: formData.projectStaffs
            .filter(staff => staff.name)
            .reduce((tasks, staff, index) => {
              const user = users.find(u => u.fullname === staff.name);
              if (staff.task) {
                if (user) {
                  tasks[user.user_id] = staff.task;
                } else {
                  // If no user found, use index as key to preserve task
                  tasks[`staff_${index}`] = staff.task;
                }
              }
              return tasks;
            }, {})
        };

        // Debug: Log the payload being sent
        console.log('🔍 DEBUG - STF/MDS ProjectProposal payload:');
        console.log('project_leader_name:', payload.project_leader_name);
        console.log('project_leader_task:', payload.project_leader_task);
        console.log('assistant_project_leader_names:', payload.assistant_project_leader_names);
        console.log('assistant_project_leader_tasks:', payload.assistant_project_leader_tasks);
        console.log('coordinator_names:', payload.coordinator_names);
        console.log('coordinator_tasks:', payload.coordinator_tasks);

        // Submit to the projects API immediately
        const { data: createdProject } = await axios.post('/projects', payload);
        const projectId = createdProject.project_id;
        
        showSuccess('Project created successfully! Redirecting to Line-Item Budget form...');
        
        // Redirect to line-item budget form with the created project ID
        navigate('/projects/budget', { state: { projectId: projectId } });
        return;
      }

      // Debug: Log monitoring evaluation data
      console.log('meTableData being sent:', meTableData);
      console.log('meTableData keys:', Object.keys(meTableData));
      console.log('meTableData has data:', Object.keys(meTableData).length > 0);
      console.log('meTableData impact objectives:', meTableData.impact?.objectives);
      console.log('meTableData impact performance_indicators:', meTableData.impact?.performance_indicators);
      
      // Prepare the payload for the API
      const payload = {
        request_type: formData.requestType === 'client' ? 'Client Requested' : 'Department Initiative',
        event_type: formData.eventType,
        title: formData.title,
        location: formData.location,
        start_date: formData.startDate,
        end_date: formData.endDate,
        start_time: formData.startTime,
        end_time: formData.endTime,
        extension_agenda: [formData.extensionAgenda],
        sdg_goals: formData.sdg,
        offices_involved: formData.officesInvolved,
        programs_involved: formData.programsInvolved,
        partner_agencies: formData.partnerAgencies,
        beneficiaries: formData.beneficiaries,
        total_cost: removeCommas(formData.totalCost),
        fund_source: formData.fundSource === 'Others' ? `Others: ${fundSourceOther}` : formData.fundSource,
        rationale: formData.rationale,
        objectives: formData.objectives,
        expected_output: formData.expectedOutput,
        strategies_methods: formData.strategiesMethods,
        financial_plan_details: formData.financialPlanDetails,
        functional_relationships: formData.functionalRelationships,
        monitoring_evaluation_table: { rows: meRows },
        sustainability_plan: formData.sustainabilityPlan,
        // User selections with tasks
        project_leader_id: formData.selectedProjectLeader,
        project_leader_name: (formData.projectLeaderHonorific ? `${formData.projectLeaderHonorific} ` : '') + formData.projectLeader + (formData.projectLeaderTask ? ` - ${formData.projectLeaderTask}` : ''),
        project_leader_honorific: formData.projectLeaderHonorific,
        project_leader_task: formData.projectLeaderTask,
        assistant_project_leader_ids: formData.assistantLeaders
          .filter(leader => leader.name)
          .map(leader => {
            const user = users.find(u => u.fullname === leader.name);
            return user ? user.user_id : null;
          })
          .filter(id => id !== null),
        assistant_project_leader_names: formData.assistantLeaders
          .filter(leader => leader.name)
          .map(leader => {
            const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
            return (leader.honorific ? `${leader.honorific} ` : '') + leader.name + (tasksStr ? ` - ${tasksStr}` : '');
          }),
        assistant_project_leader_honorifics: formData.assistantLeaders
          .filter(leader => leader.name)
          .reduce((honorifics, leader, index) => {
            const user = users.find(u => u.fullname === leader.name);
            if (user && leader.honorific) {
              honorifics[user.user_id] = leader.honorific;
            } else if (leader.honorific) {
              honorifics[index] = leader.honorific;
            }
            return honorifics;
          }, {}),
        assistant_project_leader_tasks: formData.assistantLeaders
          .filter(leader => leader.name)
          .reduce((tasks, leader, index) => {
            const user = users.find(u => u.fullname === leader.name);
            const tasksStr = leader.tasks.filter(t => t.trim()).join('; ');
            if (tasksStr) {
              if (user) {
                tasks[user.user_id] = tasksStr;
              } else {
                // If no user found, use index as key to preserve task
                tasks[`assistant_${index}`] = tasksStr;
              }
            }
            return tasks;
          }, {}),
        coordinator_ids: [...formData.coordinators]
          .filter(coord => coord.name)
          .map(coord => {
            const user = users.find(u => u.fullname === coord.name);
            return user ? user.user_id : null;
          })
          .filter(id => id !== null),
        coordinator_names: [...formData.coordinators]
          .filter(coord => coord.name)
          .map(coord => coord.name + (coord.task ? ` - ${coord.task}` : '')),
        coordinator_tasks: [...formData.coordinators]
          .filter(coord => coord.name)
          .reduce((tasks, coord, index) => {
            const user = users.find(u => u.fullname === coord.name);
            const tasksStr = coord.tasks.filter(t => t.trim()).join('; ');
            if (tasksStr) {
              if (user) {
                tasks[user.user_id] = tasksStr;
              } else {
                // If no user found, use index as key to preserve task
                tasks[`coordinator_${index}`] = tasksStr;
              }
            }
            return tasks;
          }, {}),
        // New: project staff fields
        project_staff_ids: formData.projectStaffs
          .filter(staff => staff.name)
          .map(staff => {
            const user = users.find(u => u.fullname === staff.name);
            return user ? user.user_id : null;
          })
          ,
        project_staff_names: formData.projectStaffs
          .filter(staff => staff.name)
          .map(staff => (staff.honorific ? `${staff.honorific} ` : '') + staff.name),
        project_staff_honorifics: formData.projectStaffs
          .filter(staff => staff.name)
          .reduce((honorifics, staff, index) => {
            const user = users.find(u => u.fullname === staff.name);
            if (user && staff.honorific) {
              honorifics[user.user_id] = staff.honorific;
            } else if (staff.honorific) {
              honorifics[index] = staff.honorific;
            }
            return honorifics;
          }, {}),
        project_staff_tasks: formData.projectStaffs
          .filter(staff => staff.name)
          .reduce((tasks, staff, index) => {
            const user = users.find(u => u.fullname === staff.name);
            const tasksStr = staff.tasks.filter(t => t.trim()).join('; ');
            if (tasksStr) {
              if (user) {
                tasks[user.user_id] = tasksStr;
              } else {
                // If no user found, use index as key to preserve task
                tasks[`staff_${index}`] = tasksStr;
              }
            }
            return tasks;
          }, {})
      };

      // Debug: Log the payload being sent
      console.log('🔍 DEBUG - Client sending payload:');
      console.log('project_leader_name:', payload.project_leader_name);
      console.log('project_leader_task:', payload.project_leader_task);
      console.log('assistant_project_leader_names:', payload.assistant_project_leader_names);
      console.log('assistant_project_leader_tasks:', payload.assistant_project_leader_tasks);
      console.log('coordinator_names:', payload.coordinator_names);
      console.log('coordinator_tasks:', payload.coordinator_tasks);

      // Submit to the projects API (use relative path like ProjectProposal)
      await axios.post('/projects', payload);
      
      showSuccess('Project created successfully!');
      navigate('/projects');
      
    } catch (error) {
      console.error('Error submitting project:', error);
      const status = error?.response?.status;
      const details = error?.response?.data?.details || error?.response?.data?.error;
      const msg = details || (status ? `Request failed (${status}). See console for details.` : 'Failed to create project. Please try again.');
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  

  // Handle monitoring and evaluation table changes
  // eslint-disable-next-line no-unused-vars
  const handleMeTableChange = (rowKey, field, value) => {
    console.log('handleMeTableChange called:', { rowKey, field, value });
    console.log('Current meTableData before update:', meTableData);
    setMeTableData(prev => {
      const newData = {
        ...prev,
        [rowKey]: {
          ...prev[rowKey],
          [field]: value
        }
      };
      console.log('Updated meTableData:', newData);
      return newData;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Just return the raw database value without any conversion
    return dateString;
  };

  // eslint-disable-next-line no-unused-vars
  const splitToItems = (text) => {
    if (!text) return [];
    return text
      .split(/\r?\n|,/)
      .map(s => s.trim())
      .filter(Boolean);
  };

  // eslint-disable-next-line no-unused-vars
  const parseNameTasks = (text) => {
    const lines = splitToItems(text);
    return lines.map(line => {
      const parts = line.split(/\s*[:|-]\s*/); // supports "Name: Task" or "Name - Task"
      const name = parts.shift() || '';
      const task = parts.join(': ') || '';
      return { name, task };
    });
  };

  // eslint-disable-next-line no-unused-vars
  const getInitiativeTypeText = () => {
    return formData.requestType === 'client' 
      ? 'Extension Service Program/Project/Activity is requested by clients.'
      : 'Extension Service Program/Project/Activity is Department\'s initiative.';
  };

  // eslint-disable-next-line no-unused-vars
  const getEventTypeText = () => {
    return formData.eventType;
  };

  // eslint-disable-next-line no-unused-vars
  const getExtensionAgendaText = () => {
    if (formData.extensionAgenda.length === 0) return '';
    return formData.extensionAgenda.join(', ');
  };

  const sdgOptions = [
    'No Poverty',
    'Zero Hunger',
    'Good Health and Well-Being',
    'Quality Education',
    'Gender Equality',
    'Clean Water and Sanitation',
    'Affordable and Clean Energy',
    'Decent Work and Economic Growth',
    'Industry, Innovation and Infrastructure',
    'Reduced Inequalities',
    'Sustainable Cities and Communities',
    'Responsible Consumption and Production',
    'Climate Action',
    'Life Below Water',
    'Life on Land',
    'Peace, Justice and Strong Institutions',
    'Partnerships for the Goals'
  ];

  const honorificOptions = [
    { value: '', label: 'None' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Engr.', label: 'Engr.' },
    { value: 'Prof.', label: 'Prof.' },
    { value: 'Assoc. Prof.', label: 'Assoc. Prof.' },
    { value: 'Atty.', label: 'Atty.' },
    { value: 'Rev.', label: 'Rev.' }
  ];

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Project Proposal</h4>
        </div>
            <div className="card-body">
                {notification && notification.message && (
                  <Alert variant={notification.type || 'danger'} dismissible onClose={dismiss}>
          {notification.message}
        </Alert>
      )}
                <Form onSubmit={handleSubmit}>
            {/* Initiative Type - at the very top */}
                  <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                id="request_type-client"
                name="requestType"
                value="client"
                checked={formData.requestType === 'client'}
                onChange={handleInputChange}
                onClick={handleInputChange}
                label="Extension Service Program/Project/Activity is requested by clients."
              />
              <Form.Check
                type="radio"
                id="request_type-department"
                name="requestType"
                value="department"
                checked={formData.requestType === 'department'}
                onChange={handleInputChange}
                onClick={handleInputChange}
                label="Extension Service Program/Project/Activity is Department's initiative."
              />
                  </Form.Group>

            {/* Event Type - at the very top */}
                  <Form.Group className="mb-3">
              <Row>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-program"
                    name="eventType"
                    value="Program"
                    checked={formData.eventType === 'Program'}
                    onChange={handleInputChange}
                    onClick={handleInputChange}
                    label="Program"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-project"
                    name="eventType"
                    value="Project"
                    checked={formData.eventType === 'Project'}
                    onChange={handleInputChange}
                    onClick={handleInputChange}
                    label="Project"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    id="event_type-activity"
                    name="eventType"
                    value="Activity"
                    checked={formData.eventType === 'Activity'}
                    onChange={handleInputChange}
                    onClick={handleInputChange}
                    label="Activity"
                  />
                </Col>
              </Row>
                  </Form.Group>

            <hr />
            <Row>
              <Col md={6}>
                  <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Title:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter project title"
                    maxLength={500}
                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                  />
                  </Form.Group>
              </Col>
              <Col md={6}>
                  <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Location:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter project location"
                    maxLength={500}
                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                  />
                  </Form.Group>
              </Col>
            </Row>

                    <Row>
                      <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Start Date:</strong></Form.Label>
                        <Form.Control 
                          type="date" 
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                      </Col>
                      <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>End Date:</strong></Form.Label>
                        <Form.Control 
                          type="date" 
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                      </Col>
                    </Row>

            <Row>
                      <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Start Time (Optional):</strong></Form.Label>
                        <Form.Control 
                          type="time" 
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    disabled={!(formData.eventType === 'Project' || formData.eventType === 'Activity')}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                      </Col>
                      <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>End Time (Optional):</strong></Form.Label>
                        <Form.Control 
                          type="time" 
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    disabled={!(formData.eventType === 'Project' || formData.eventType === 'Activity')}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>
                      </Col>
                    </Row>

            {/* Extension Service Agenda - Section IV */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Extension Service Agenda</h4>
                  <Form.Group className="mb-3">
              {extensionAgendaOptions.map((option, index) => (
                      <Form.Check 
                  key={index}
                        type="radio" 
                  label={option}
                  name="extensionAgenda"
                  value={option}
                  checked={formData.extensionAgenda === option}
                  onChange={handleInputChange}
                  onClick={handleInputChange}
                  className="mb-2"
                      />
                    ))}
                  </Form.Group>

            {/* SDG Goals - Section V */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Sustainable Development Goals (SDG)</h4>
                  <Form.Group className="mb-3">
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  {sdgOptions.slice(0, 9).map((option, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      label={option}
                      name="sdg"
                      value={option}
                      checked={formData.sdg.includes(option)}
                      onChange={handleInputChange}
                      onClick={handleInputChange}
                      className="mb-2"
                    />
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  {sdgOptions.slice(9).map((option, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      label={option}
                      name="sdg"
                      value={option}
                      checked={formData.sdg.includes(option)}
                      onChange={handleInputChange}
                      onClick={handleInputChange}
                      className="mb-2"
                    />
                  ))}
                </div>
              </div>
                  </Form.Group>

            {/* Offices/Colleges/Organizations Involved - Section VI */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Offices/Colleges/Organizations Involved</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="officesInvolved"
                value={formData.officesInvolved}
                onChange={handleInputChange}
                placeholder="Enter offices/colleges/organizations involved"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>

            {/* Programs Involved - Section VII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Programs Involved</h4>
            <Form.Group className="mb-3">
                      <Form.Control
                as="textarea"
                rows={3}
                name="programsInvolved"
                value={formData.programsInvolved}
                onChange={handleInputChange}
                placeholder="Enter programs involved"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>

            {/* Project Leader, Assistant Project Leaders, Coordinators - Section VIII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Project Leadership</h4>
            {/* Project Leader (single) */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Project Leader:</strong></Form.Label>
              <Select
                name="selectedProjectLeader"
                value={formData.selectedProjectLeader ? getAvailableUsers('projectLeader').find(option => option.value === formData.selectedProjectLeader) : null}
                onChange={handleProjectLeaderChange}
                options={getAvailableUsers('projectLeader')}
                placeholder="Select Project Leader"
                isDisabled={loading}
                isSearchable={true}
                isClearable={true}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #888',
                    fontSize: '1em'
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 99999,
                    backgroundColor: 'white',
                    position: 'absolute',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #ccc'
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    backgroundColor: 'white',
                    padding: 0
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f3ff' : 'white',
                    color: state.isSelected ? 'white' : '#212529',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: '1em',
                    fontWeight: state.isSelected ? '600' : '400'
                  })
                }}
              />
                  </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Honorific:</strong></Form.Label>
                  <Form.Select
                    name="projectLeaderHonorific"
                    value={formData.projectLeaderHonorific}
                    onChange={handleInputChange}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  >
                    {honorificOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                  <Form.Group className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Form.Label className="mb-0"><strong>Tasks:</strong></Form.Label>
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                        title="Add task"
                        onClick={addProjectLeaderTask}
                      >
                        <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    </div>
                    {formData.projectLeaderTasks.map((task, taskIdx) => (
                      <div key={`pl-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={task}
                          onChange={(e) => updateProjectLeaderTask(taskIdx, e.target.value)}
                          placeholder={`Task ${taskIdx + 1}`}
                          maxLength={2000}
                          style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                        />
                        {formData.projectLeaderTasks.length > 1 && (
                          <Button
                            variant="link"
                            className="p-0 ms-2"
                            style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                            title="Remove task"
                            onClick={() => removeProjectLeaderTask(taskIdx)}
                          >
                            <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                          </Button>
                        )}
                      </div>
                    ))}
                  </Form.Group>
              </Col>
            </Row>

            {/* Assistant Project Leaders (multiple) & Coordinators (multiple) */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                  <Form.Label className="mb-0"><strong>Assistant Project Leaders</strong></Form.Label>
                  <div>
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                      title="Add assistant project leader"
                      aria-label="Add assistant project leader"
                      onClick={() => addArrayRow('assistantLeaders')}
                    >
                      <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                    </Button>
                    {formData.assistantLeaders.length > 1 && (
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                        title="Remove last assistant project leader"
                        aria-label="Remove assistant project leader"
                        onClick={() => removeArrayRow('assistantLeaders', formData.assistantLeaders.length - 1)}
                      >
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    )}
                  </div>
                </Form.Group>
                {formData.assistantLeaders.map((row, idx) => (
                  <Row key={`apl-row-${idx}`} className="mb-2">
                    <Col md={4}>
                      <Select
                        value={getAvailableUsers('assistantLeaders', idx).find(option => option.user.fullname === row.name)}
                        onChange={(selectedOption) => updateArrayField('assistantLeaders', idx, 'name', selectedOption)}
                        options={getAvailableUsers('assistantLeaders', idx)}
                        placeholder="Select Assistant Project Leader"
                        isDisabled={loading}
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            border: '1px solid #888',
                            fontSize: '1em'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 99999,
                            backgroundColor: 'white',
                            position: 'absolute',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid #ccc'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            backgroundColor: 'white',
                            padding: 0
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f3ff' : 'white',
                            color: state.isSelected ? 'white' : '#212529',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '1em',
                            fontWeight: state.isSelected ? '600' : '400'
                          })
                        }}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Select
                        value={row.honorific || ''}
                        onChange={(e) => updateArrayField('assistantLeaders', idx, 'honorific', e.target.value)}
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      >
                        {honorificOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <Form.Label className="mb-0"><strong>Tasks:</strong></Form.Label>
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                          title="Add task"
                          onClick={() => addTask('assistantLeaders', idx)}
                        >
                          <i className="bi bi-plus-circle-fill" style={{ fontSize: 16 }}></i>
                        </Button>
                      </div>
                      {row.tasks.map((task, taskIdx) => (
                        <div key={`apl-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={task}
                            onChange={(e) => updateTask('assistantLeaders', idx, taskIdx, e.target.value)}
                            placeholder={`Task ${taskIdx + 1}`}
                            maxLength={2000}
                            style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                          />
                          {row.tasks.length > 1 && (
                            <Button
                              variant="link"
                              className="p-0 ms-2"
                              style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                              title="Remove task"
                              onClick={() => removeTask('assistantLeaders', idx, taskIdx)}
                            >
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 16 }}></i>
                            </Button>
                          )}
                        </div>
                      ))}
                    </Col>
                  </Row>
                ))}
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                  <Form.Label className="mb-0"><strong>Coordinator</strong></Form.Label>
                  <div>
                    {formData.coordinators.length > 1 && (
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                        title="Remove last coordinator"
                        aria-label="Remove coordinator"
                        onClick={() => removeArrayRow('coordinators', formData.coordinators.length - 1)}
                      >
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                            </Button>
                    )}
                  </div>
                  </Form.Group>
                {formData.coordinators.map((row, idx) => (
                  <Row key={`coord-row-${idx}`} className="mb-2">
                    <Col md={6}>
                      <Select
                        value={getAvailableUsers('coordinators', idx).find(option => option.user.fullname === row.name)}
                        onChange={(selectedOption) => updateArrayField('coordinators', idx, 'name', selectedOption)}
                        options={getAvailableUsers('coordinators', idx)}
                        placeholder="Select Coordinator"
                        isDisabled={loading}
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            border: '1px solid #888',
                            fontSize: '1em'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 99999,
                            backgroundColor: 'white',
                            position: 'absolute',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid #ccc'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            backgroundColor: 'white',
                            padding: 0
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f3ff' : 'white',
                            color: state.isSelected ? 'white' : '#212529',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '1em',
                            fontWeight: state.isSelected ? '600' : '400'
                          })
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <Form.Label className="mb-0"><strong>Tasks:</strong></Form.Label>
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                          title="Add task"
                          onClick={() => addTask('coordinators', idx)}
                        >
                          <i className="bi bi-plus-circle-fill" style={{ fontSize: 16 }}></i>
                        </Button>
                      </div>
                      {row.tasks.map((task, taskIdx) => (
                        <div key={`coord-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={task}
                            onChange={(e) => updateTask('coordinators', idx, taskIdx, e.target.value)}
                            placeholder={`Task ${taskIdx + 1}`}
                            maxLength={2000}
                            style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                          />
                          {row.tasks.length > 1 && (
                            <Button
                              variant="link"
                              className="p-0 ms-2"
                              style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                              title="Remove task"
                              onClick={() => removeTask('coordinators', idx, taskIdx)}
                            >
                              <i className="bi bi-dash-circle-fill" style={{ fontSize: 16 }}></i>
                            </Button>
                          )}
                        </div>
                      ))}
                    </Col>
                  </Row>
                ))}
              </Col>
            </Row>

            {/* Project Staff (multiple) */}
            <Row className="mt-3">
              <Col md={12}>
                <Form.Group className="mb-2 d-flex align-items-center justify-content-between">
                  <Form.Label className="mb-0"><strong>Project Staff</strong></Form.Label>
                  <div>
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                      title="Add project staff"
                      aria-label="Add project staff"
                      onClick={() => addArrayRow('projectStaffs')}
                    >
                      <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                    </Button>
                    {formData.projectStaffs.length > 1 && (
                      <Button
                        variant="link"
                        className="p-0"
                        style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                        title="Remove last project staff"
                        aria-label="Remove project staff"
                        onClick={() => removeArrayRow('projectStaffs', formData.projectStaffs.length - 1)}
                      >
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    )}
                  </div>
                </Form.Group>
                {/* Render in pairs per row: [Select, Task] x 2 = 4 fields across */}
                {(() => {
                  const pairs = [];
                  for (let i = 0; i < formData.projectStaffs.length; i += 2) {
                    pairs.push(formData.projectStaffs.slice(i, i + 2));
                  }
                  return pairs.map((pair, rowIdx) => (
                    <Row key={`staff-pair-row-${rowIdx}`} className="mb-2">
                      {pair.map((row, idxInPair) => {
                        const idx = rowIdx * 2 + idxInPair;
                        return (
                          <React.Fragment key={`staff-frag-${idx}`}>
                            <Col md={3}>
                              <Select
                                value={getAvailableUsers('projectStaffs', idx).find(option => option.user.fullname === row.name)}
                                onChange={(selectedOption) => updateArrayField('projectStaffs', idx, 'name', selectedOption)}
                                options={getAvailableUsers('projectStaffs', idx)}
                                placeholder="Select Project Staff"
                                isDisabled={loading}
                                isSearchable={true}
                                isClearable={true}
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    border: '1px solid #888',
                                    fontSize: '1em'
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                    backgroundColor: 'white'
                                  }),
                                  menuList: (provided) => ({
                                    ...provided,
                                    backgroundColor: 'white',
                                    padding: 0
                                  }),
                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f3ff' : 'white',
                                    color: state.isSelected ? 'white' : '#212529',
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '1em',
                                    fontWeight: state.isSelected ? '600' : '400'
                                  })
                                }}
                              />
                            </Col>
                            <Col md={2}>
                              <Form.Select
                                value={row.honorific || ''}
                                onChange={(e) => updateArrayField('projectStaffs', idx, 'honorific', e.target.value)}
                                style={{ border: '1px solid #888', fontSize: '1em' }}
                              >
                                {honorificOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col md={3}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <Form.Label className="mb-0" style={{ fontSize: '0.9em' }}><strong>Tasks:</strong></Form.Label>
                                <Button
                                  variant="link"
                                  className="p-0"
                                  style={{ border: 'none', boxShadow: 'none', color: '#0d6efd' }}
                                  title="Add task"
                                  onClick={() => addTask('projectStaffs', idx)}
                                >
                                  <i className="bi bi-plus-circle-fill" style={{ fontSize: 14 }}></i>
                                </Button>
                              </div>
                              {row.tasks.map((task, taskIdx) => (
                                <div key={`staff-${idx}-task-${taskIdx}`} className="mb-2 d-flex align-items-start">
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={task}
                                    onChange={(e) => updateTask('projectStaffs', idx, taskIdx, e.target.value)}
                                    placeholder={`Task ${taskIdx + 1}`}
                                    maxLength={2000}
                                    style={{ border: '1px solid #888', fontSize: '1em', resize: 'vertical', flex: 1 }}
                                  />
                                  {row.tasks.length > 1 && (
                                    <Button
                                      variant="link"
                                      className="p-0 ms-1"
                                      style={{ border: 'none', boxShadow: 'none', color: '#d32f2f' }}
                                      title="Remove task"
                                      onClick={() => removeTask('projectStaffs', idx, taskIdx)}
                                    >
                                      <i className="bi bi-dash-circle-fill" style={{ fontSize: 14 }}></i>
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </Col>
                          </React.Fragment>
                        );
                      })}
                      {/* If odd count, leave remaining columns empty to keep layout */}
                      {pair.length === 1 && (
                        <>
                          <Col md={3}></Col>
                          <Col md={2}></Col>
                          <Col md={3}></Col>
                        </>
                      )}
                    </Row>
                  ));
                })()}
              </Col>
            </Row>

            {/* Partner Agencies - Section IX */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Partner Agencies</h4>
                  <Form.Group className="mb-3">
                      <Form.Control
                as="textarea"
                rows={3}
                name="partnerAgencies"
                value={formData.partnerAgencies}
                    onChange={handleInputChange}
                placeholder="Enter partner agencies"
                    maxLength={500}
                    style={{ border: '1px solid #888', fontSize: '1em' }}
                  />
                </Form.Group>

            {/* Beneficiaries - Section X */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Beneficiaries</h4>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="beneficiaries"
                value={formData.beneficiaries}
                onChange={handleInputChange}
                placeholder="Enter beneficiaries"
                maxLength={500}
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>

            {/* Total Cost - Section XI */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Total Cost</h4>
            <Row>
              <Col md={6}>
                  <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#d32f2f' }}><strong>Total Cost:</strong></Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">₱</span>
                    <Form.Control
                      type="text"
                      name="totalCost"
                      value={formData.totalCost}
                      onChange={handleInputChange}
                      placeholder="Enter total cost (e.g., 1,000,000.50)"
                      maxLength={50}
                      style={{ border: '1px solid #888', fontSize: '1em' }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Fund Source - after Total Cost */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Source of Fund</h4>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#d32f2f' }}><strong>Source of fund:</strong></Form.Label>
              <div className="mb-2">
                      {['STF', 'MDS', 'Others'].map((fs) => (
                                          <Form.Check
                            key={fs}
                            inline
                            type="radio"
                            label={fs === 'Others' ? 'Others, (Please specify)' : fs}
                            name="fundSource"
                            value={fs}
                            checked={formData.fundSource === fs}
                            onChange={handleInputChange}
                            onClick={handleInputChange}
                          />
                        ))}
                    </div>
              {formData.fundSource === 'Others' && (
                <Form.Control
                  placeholder="Specify other source"
                  value={fundSourceOther}
                  onChange={(e) => setFundSourceOther(e.target.value)}
                  maxLength={255}
                  style={{ border: '1px solid #888', fontSize: '1em' }}
                />
                    )}
                  </Form.Group>

                  {/* Rationale - Section XII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Rationale</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="rationale"
                value={formData.rationale}
                onChange={handleInputChange}
                placeholder="Enter project rationale"
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>

                  {/* Objectives - Section XIII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Objectives</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="objectives"
                value={formData.objectives}
                onChange={handleInputChange}
                placeholder="Enter project objectives"
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>

                  {/* Program/Project Expected Output - Section XIV */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Program/Project Expected Output</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="expectedOutput"
                value={formData.expectedOutput}
                onChange={handleInputChange}
                placeholder="Enter expected output"
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>

                  {/* Description, Strategies and Methods - Section XV */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Description, Strategies and Methods (Activities / Schedule)</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="strategiesMethods"
                value={formData.strategiesMethods}
                onChange={handleInputChange}
                placeholder="Enter strategies and methods"
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
                  </Form.Group>


            {/* Financial Plan - Section XVI */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Financial Plan</h4>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div></div>
                <div className="d-flex gap-2">
                  <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addFinancialPlanRow} title="Add row" aria-label="Add Financial Plan row">
                    <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                  <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeFinancialPlanRow} title="Remove last row" aria-label="Remove Financial Plan row" disabled={financialPlanRows.length <= 4}>
                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                </div>
              </div>
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #dee2e6' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', width: '50%' }}>Item Description</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Quantity</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '10%' }}>Unit</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Price (Php)</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', width: '15%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialPlanRows.map((row, idx) => (
                      <tr key={`fp-row-${idx}`}>
                        <td style={{ border: '1px solid #000', padding: '6px' }}>
                          <input value={row.description} onChange={(e) => updateFinancialRow(idx, 'description', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          <input value={row.quantity} onChange={(e) => updateFinancialRow(idx, 'quantity', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                          <input value={row.unit} onChange={(e) => updateFinancialRow(idx, 'unit', e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                          <input value={row.price} onChange={(e) => updateFinancialRow(idx, 'price', e.target.value)} inputMode="decimal" pattern="[0-9]*" style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} />
                        </td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                          <input value={row.total} onChange={(e) => updateFinancialRow(idx, 'total', e.target.value)} inputMode="decimal" pattern="[0-9]*" style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'right' }} />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>TOTAL</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>{
                        (() => {
                          const g = getFinancialPlanGrandTotal();
                          return g ? g.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
                        })()
                      }</td>
                    </tr>
                  </tbody>
                </table>
              </div>
                    </Form.Group>

              

            {/* Functional Relationships - Section XVII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Functional Relationships with the Partner Agencies (Duties / Tasks of the Partner Agencies)</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="functionalRelationships"
                value={formData.functionalRelationships}
                onChange={handleInputChange}
                placeholder="Enter functional relationships details"
                style={{ border: '1px solid #888', fontSize: '1em' }}
              />
            </Form.Group>

            {/* Monitoring and Evaluation - Section XVIII */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Monitoring and Evaluation Mechanics / Plan</h4>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div></div>
                <div className="d-flex gap-2">
                  <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addMeRow} title="Add row" aria-label="Add M&E row">
                    <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                  <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeMeRow} title="Remove last row" aria-label="Remove M&E row" disabled={meRows.length <= 5}>
                    <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                  </Button>
                </div>
              </div>
              <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #dee2e6', width: '100%' }}>
                <table style={{ width: '100%', marginBottom: 0, borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '1600px' }}>
                  <thead style={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '15%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Objectives</th>
                      <th style={{ width: '20%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Performance Indicators</th>
                      <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Baseline Data</th>
                      <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Performance Target</th>
                      <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Data Source</th>
                      <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Collection Method</th>
                      <th style={{ width: '18%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Frequency of Data Collection</th>
                      <th style={{ width: '25%', fontSize: '1rem', padding: '16px', border: '1px solid #dee2e6' }}>Office/Persons Responsible</th>
                          </tr>
                        </thead>
                    <tbody>
                      {meRows.map((row, idx) => (
                        <tr key={`me-row-${idx}`} style={{ verticalAlign: 'top' }}>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.objectives} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, objectives: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.performance_indicators} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_indicators: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.baseline_data} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, baseline_data: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.performance_target} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, performance_target: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.data_source} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, data_source: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.collection_method} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, collection_method: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.frequency} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, frequency: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                          <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                            <textarea rows={4} value={row.responsible} onChange={(e) => setMeRows(prev => prev.map((r, i) => i === idx ? { ...r, responsible: e.target.value } : r))} style={{ fontSize: '1rem', resize: 'vertical', minHeight: '80px', border: '1px solid #ced4da', background: 'white', width: '100%', padding: '12px', margin: '0', outline: 'none' }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                      </table>
                    </div>
                  </Form.Group>

                  {/* Sustainability Plan - Section XIX */}
            <hr />
            <h4 style={{ color: '#d32f2f' }}>Sustainability Plan</h4>
                  <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                name="sustainabilityPlan"
                value={formData.sustainabilityPlan}
                  onChange={handleInputChange}
                placeholder="Enter sustainability plan"
                style={{ border: '1px solid #888', fontSize: '1em' }}
                />
                  </Form.Group>


            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <Button 
                type="submit"
                disabled={submitting}
                style={{ 
                  background: '#28a745', 
                  color: '#fff', 
                  padding: '8px 18px', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                {submitting ? 'Creating Project...' : 'Create Project'}
              </Button>
                  </div>
                </Form>
            </div>
          </div>
        </div>
      </div>
      {/* Submit Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Project Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Project Submission</h5>
            <p className="text-muted">Are you sure that all fields are correct?</p>
            {formData.fundSource === 'STF' || formData.fundSource === 'MDS' ? (
              <div className="alert alert-info">
                <strong>Note:</strong> Since this Program/Project/Activity (PPA) involves the use of budget for its implementation, you will now be directed to the Line-Item Budget creation form.
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleConfirmSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Project'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectProposal;